import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentlinksPaymentsV1PaymentLinkPaymentsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/payment-link-payments',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/payment-links/v1/payment-link-payments',
        destPath: '/v1/payment-link-payments',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/paymentlinks/payments/v1/payment-link-payments',
        destPath: '/v1/payment-link-payments',
      },
    ],
    _: [
      {
        srcPath: '/paymentlinks/payments/v1/payment-link-payments',
        destPath: '/v1/payment-link-payments',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_get-paid_payment-link-payments';

/** Retrieves a list of up to 100 payment link payments given the specified paging, filtering, and sorting ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language) | [REST](https://dev.wix.com/api/rest/getting-started/api-query-language)). */
export function queryPaymentLinkPayments(
  payload: object
): RequestOptionsFactory<any> {
  function __queryPaymentLinkPayments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.paymentlinks.payments.v1.payment_link_payment',
      method: 'POST' as any,
      methodFqn:
        'wix.paymentlinks.payments.v1.PaymentLinkPayments.QueryPaymentLinkPayments',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentlinksPaymentsV1PaymentLinkPaymentsUrl({
        protoPath: '/v1/payment-link-payments/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentLinkPayments.createdDate' },
              { path: 'paymentLinkPayments.transactionDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'paymentLinkPayments.regularPaymentLinkPayment.billingAddress.geocode.latitude',
              },
              {
                path: 'paymentLinkPayments.regularPaymentLinkPayment.billingAddress.geocode.longitude',
              },
              {
                path: 'paymentLinkPayments.giftCardPaymentLinkPayment.billingAddress.geocode.latitude',
              },
              {
                path: 'paymentLinkPayments.giftCardPaymentLinkPayment.billingAddress.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryPaymentLinkPayments;
}

/** Retrieves a list of up to 100 payment links, given the specified filtering, search expression, sorting, and cursor paging ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language) | [REST](https://dev.wix.com/api/rest/getting-started/api-query-language)). */
export function searchPaymentLinkPayments(
  payload: object
): RequestOptionsFactory<any> {
  function __searchPaymentLinkPayments({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.paymentlinks.payments.v1.payment_link_payment',
      method: 'POST' as any,
      methodFqn:
        'wix.paymentlinks.payments.v1.PaymentLinkPayments.SearchPaymentLinkPayments',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentlinksPaymentsV1PaymentLinkPaymentsUrl({
        protoPath: '/v1/payment-link-payments/search',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentLinkPayments.createdDate' },
              { path: 'paymentLinkPayments.transactionDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'paymentLinkPayments.regularPaymentLinkPayment.billingAddress.geocode.latitude',
              },
              {
                path: 'paymentLinkPayments.regularPaymentLinkPayment.billingAddress.geocode.longitude',
              },
              {
                path: 'paymentLinkPayments.giftCardPaymentLinkPayment.billingAddress.geocode.latitude',
              },
              {
                path: 'paymentLinkPayments.giftCardPaymentLinkPayment.billingAddress.geocode.longitude',
              },
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchPaymentLinkPayments;
}

/** Issues a receipt for the specified payment link payment. */
export function issueReceipt(payload: object): RequestOptionsFactory<any> {
  function __issueReceipt({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.paymentlinks.payments.v1.payment_link_payment',
      method: 'POST' as any,
      methodFqn:
        'wix.paymentlinks.payments.v1.PaymentLinkPayments.IssueReceipt',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentlinksPaymentsV1PaymentLinkPaymentsUrl({
        protoPath:
          '/v1/payment-link-payments/{paymentLinkPaymentId}/issue-receipt',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __issueReceipt;
}
